/* React imports */
import { useEffect, useMemo, useState } from "react";

/* Third party imports */
import { Field, Formik, Form, FieldArray, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

/* Local imports */
import Button from "../../../components/button";
import { ButtonSize, ButtonVariant } from "../../../components/button/types";
import { getEnum } from "../../../services/onboarding.service";
import {
  getYearsOfExperienceRanges,
  JobHistory,
  KeyValue,
  StepBlueCareerParameters,
  StepBlueCareerProps,
  StepCareerProps,
} from "./types";
import { AuthContext } from "../../../contexts/AuthContext";
import CloseIcon from "@mui/icons-material/Close";

export const AddJobSection = ({ push }: { push: (obj: any) => void }) => {
  const { values, setFieldValue } = useFormikContext<
    StepBlueCareerParameters & {
      currentJobTitle?: string;
      currentExperience?: string;
    }
  >();
  const { t } = useTranslation("onboarding");
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null
  );
  const handleAddJob = () => {
    const title = values.currentJobTitle?.trim();
    const experience = values.currentExperience;

    if (title && experience) {
      push({
        lastPosition: title || null,
        experienceInYears: experience || null,
      });

      setFieldValue("currentJobTitle", "");
      setFieldValue("currentExperience", "");
      setValidationMessage(null);
    } else {
      setValidationMessage(
        t(
          "stepBlueCareer.addJobValidationError",
          "Please add the title and years"
        )
      );
    }
  };

  return (
    <div className="mt-4 flex justify-end items-center space-x-4">
      {validationMessage && (
        <span className="text-sm text-red-600">{validationMessage}</span>
      )}
      <Button
        type="button"
        variant={ButtonVariant.SECONDARY}
        size={ButtonSize.SMALL}
        onClick={handleAddJob}
        className="bg-Gray-light"
      >
        {t("stepBlueCareer.addJob", "+ Add Job Entry")}
      </Button>
    </div>
  );
};

export default function StepBlueCareer({
  onNext,
  onSkip,
  onBack,
  initialData,
}: StepBlueCareerProps) {
  const { t } = useTranslation("onboarding");

  const [industries, setIndustries] = useState<KeyValue[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    async function fetchEnums() {
      const industries = await getEnum("industries");
      setIndustries(industries);

      setIsLoading(false);
    }
    fetchEnums();
  }, []);

  const formInitialValues = useMemo(() => {
    const allJobHistories = initialData?.jobHistories || [];

    const currentJobHistory =
      allJobHistories.find((h) => h.isCurrent) || allJobHistories[0] || null;
    const previousJobHistories = allJobHistories.filter(
      (h) => h !== currentJobHistory
    );

    return {
      lastPosition:
        initialData?.lastPosition ?? (currentJobHistory?.lastPosition || ""),
      experienceInYears:
        initialData?.experienceInYears ??
        (currentJobHistory?.experienceInYears || ""),
      industry: initialData?.industry ?? "",

      jobHistories: previousJobHistories.map((h) => ({
        id: h.id,
        lastPosition: h.lastPosition || "",
        experienceInYears: h.experienceInYears || "",
      })),

      currentJobTitle: "",
      currentExperience: "",
    };
  }, [initialData]);

  return (
    <>
      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <Formik
          initialValues={formInitialValues}
          onSubmit={(values) => {
            const finalHistoryList: JobHistory[] = [];
            const currentJobEntry: JobHistory = {
              lastPosition: values.lastPosition?.trim() || null,
              experienceInYears: values.experienceInYears || null,
              isCurrent: true,
            };

            if (
              currentJobEntry.lastPosition ||
              currentJobEntry.experienceInYears
            ) {
              finalHistoryList.push(currentJobEntry);
            }

            const addedPreviousJobs = (values.jobHistories || [])
              .map((job) => ({
                lastPosition: job.lastPosition?.trim() || null,
                experienceInYears: job.experienceInYears || null,
                isCurrent: false,
              }))
              .filter(
                (job) =>
                  job.lastPosition !== null || job.experienceInYears !== null
              );
            finalHistoryList.push(...addedPreviousJobs);

            const unaddedTitle = values.currentJobTitle?.trim();
            const unaddedExp = values.currentExperience;
            if (unaddedTitle || unaddedExp) {
              // Add the un-added entry as NOT current
              finalHistoryList.push({
                lastPosition: unaddedTitle || null,
                experienceInYears: unaddedExp || null,
                isCurrent: false, // This is also not the primary "current" job
              });
            }

            const finalPayload: StepBlueCareerParameters = {
              lastPosition: values.lastPosition?.trim() || null,
              experienceInYears: values.experienceInYears || null,
              industry: values.industry || null,
              jobHistories: finalHistoryList,
            };

            onNext(finalPayload);
          }}
        >
          {({ values, handleChange }) => (
            <div className="w-full">
              <div className="flex flex-col">
                <h1 className="text-xl font-bold leading-7 mb-1">
                  {t("stepBlueCareer.title")}
                </h1>
                <h2 className="text-base mt-2">
                  {t("stepBlueCareer.subtitle")}
                </h2>
                <Form className="w-full  mt-4">
                  <div className="flex flex-col w-full">
                    <label className="text-base mb-1">
                      {t("stepBlueCareer.lastPosition")}
                    </label>
                    <Field
                      type="text"
                      placeholder={t("stepBlueCareer.lastPositionPlaceHolder")}
                      className="border rounded-lg  px-4 py-3.5  text-base mb-4"
                      id="lastPosition"
                      name="lastPosition"
                      value={values?.lastPosition}
                      onChange={handleChange}
                    />

                    <label className="text-base mb-1">
                      {t("stepBlueCareer.experienceInYears")}
                    </label>

                    <Field
                      as="select"
                      id="experienceInYears"
                      name="experienceInYears"
                      className="border rounded-lg  px-4 py-3.5  text-base mb-4"
                      value={values.experienceInYears}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        {t("stepBlueCareer.please-select")}
                      </option>
                      {/* Use the new ranges constant */}
                      {getYearsOfExperienceRanges(t).map((range: any) => (
                        <option key={range.key} value={range.value}>
                          {range.label}
                        </option>
                      ))}
                    </Field>

                    <label className="text-base mb-1">
                      {t("stepBlueCareer.industry")}
                    </label>

                    <Field
                      as="select"
                      id="industry-select"
                      placeholder={t("stepName.please-select")}
                      name="industry"
                      className="border rounded-lg px-4 py-3.5  text-base  mb-4"
                      value={values.industry}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        {t("stepCareer.please-select")}
                      </option>
                      {industries?.map((industry) => (
                        <option key={industry?.key} value={industry?.value}>
                          {t(`industries.${industry?.value}`)}
                        </option>
                      ))}
                    </Field>
                  </div>

                  <h1 className="text-xl font-bold leading-7 mt-2">
                    {t("stepBlueCareer.previousJob", "Previous Job Title")}
                  </h1>

                  {/* --- Job History Section --- */}
                  <fieldset className=" ">
                    <FieldArray name="jobHistories">
                      {({ remove, push }) => (
                        <div>
                          {values.jobHistories.length > 0 && (
                            <div className="mb-2 space-y-3">
                              {values.jobHistories.map((job, index) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between p-3 bg-Grey-light rounded-md mt-2"
                                >
                                  <div className="">
                                    <span className="">
                                      {job.lastPosition ||
                                        t("stepBlueCareer.noTitle", "No Title")}
                                    </span>
                                    <span className="text-gray-600 ml-2">
                                      (
                                      {getYearsOfExperienceRanges(t).find(
                                        (r) => r.value === job.experienceInYears
                                      )?.label ||
                                        t(
                                          "stepBlueCareer.noExperience",
                                          "No Experience"
                                        )}
                                      )
                                    </span>
                                  </div>
                                  <div
                                    className="cursor-pointer"
                                    onClick={() => remove(index)}
                                  >
                                    <CloseIcon />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="space-y-4  mt-4">
                            {" "}
                            <div>
                              <label
                                htmlFor="currentJobTitle"
                                className="block text-base mb-1 "
                              >
                                {t(
                                  "stepBlueCareer.job-title",
                                  "Job Title / Position"
                                )}
                              </label>
                              <Field
                                id="currentJobTitle"
                                name="currentJobTitle"
                                placeholder={t(
                                  "stepBlueCareer.jobTitlePlaceholder",
                                  "e.g., Electrician"
                                )}
                                type="text"
                                className="border rounded-lg w-full px-4 py-3.5 text-base"
                              />
                            </div>
                            <div>
                              <label
                                htmlFor="currentExperience"
                                className="block text-base mb-1 "
                              >
                                {t(
                                  "stepBlueCareer.experienceInYears",
                                  "Years of Experience"
                                )}
                              </label>
                              <Field
                                as="select"
                                id="currentExperience"
                                name="currentExperience"
                                className="border rounded-lg w-full px-4 py-3.5 text-base "
                              >
                                <option value="">
                                  {t("stepBlueCareer.please-select")}
                                </option>
                                {getYearsOfExperienceRanges(t).map((range) => (
                                  <option key={range.key} value={range.value}>
                                    {range.label}
                                  </option>
                                ))}
                              </Field>
                            </div>
                            <AddJobSection push={push} />
                          </div>
                        </div>
                      )}
                    </FieldArray>
                  </fieldset>

                  <div className="w-full flex justify-between mt-6 space-x-2">
                    {onBack && (
                      <Button
                        className="w-full"
                        type="button"
                        onClick={onBack}
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.MEDIUM}
                      >
                        {t("back", "Back")}{" "}
                      </Button>
                    )}

                    <Button
                      className="w-full"
                      type="submit"
                      variant={ButtonVariant.PRIMARY}
                      size={ButtonSize.MEDIUM}
                    >
                      {t("stepCareer.next")}
                    </Button>
                  </div>
                </Form>
              </div>
            </div>
          )}
        </Formik>
      )}
    </>
  );
}
